import { useAccountUsage } from '../hooks/useAccountUsage'
import { Card } from '../components/ui/Card'
import { Meter } from '../components/ui/Meter'
import { Spinner } from '../components/ui/Spinner'
import { ErrorBanner } from '../components/ErrorBanner'
import { formatBytes, formatCount } from '../lib/format'

// AccountUsage — the dedicated, read-only account-wide usage page (/usage).
// Three meters over the account-summed counters and tier-resolved caps from
// GET /v1/me/usage: storage (durable bytes), monthly egress, and monthly
// requests. These are the quotas the data plane actually enforces — over-cap
// throttles every glow the owner controls (the shared pool), so this page is
// the one that tells the user whether they are about to 429.
//
// A zero cap renders as "Unlimited", now reserved for a GENUINELY-unlimited
// (future paid) cap. A brand-new owner with no account doc gets all-zero
// COUNTERS but concrete SPARK caps (the backend resolves caps via ResolveCaps,
// flooring un-resolved owners to Spark), so the meters fill against real caps
// rather than showing "Unlimited"; the page still always renders at HTTP 200.
export function AccountUsage() {
  const { data, isLoading, error } = useAccountUsage()

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (error || !data) {
    return (
      <ErrorBanner>
        {error ? (error as Error).message : 'Usage unavailable.'}
      </ErrorBanner>
    )
  }

  const { storage, bandwidth } = data
  const { period, resetsInDays } = monthlyPeriod()

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-1 text-2xl font-semibold">Usage</h1>
      <p className="mb-6 text-sm text-cream/70">
        Account-wide totals across every glow you own. These caps are shared —
        going over throttles all of your glows.
      </p>

      <div className="space-y-4">
        <Card>
          <h2 className="text-base font-medium text-cream">Storage</h2>
          <p className="mt-0.5 mb-4 text-sm text-muted">
            Durable bytes across bundles &amp; data, summed over all glows
          </p>
          <Meter
            label="Storage"
            used={storage.bytes_used}
            cap={storage.cap_bytes}
            format={(n) => formatBytes(n)}
          />
        </Card>

        <Card>
          <div>
            <h2 className="text-base font-medium text-cream">
              Bandwidth &amp; requests
            </h2>
            <p className="mt-0.5 text-sm text-muted">
              {period} · resets in {resetsInDays}{' '}
              {resetsInDays === 1 ? 'day' : 'days'}
            </p>
          </div>

          <div className="mt-4 space-y-5">
            <Meter
              label="Bandwidth"
              used={bandwidth.egress_bytes_used}
              cap={bandwidth.egress_cap_bytes}
              format={(n) => formatBytes(n)}
            />
            <Meter
              label="Requests"
              used={bandwidth.requests_count}
              cap={bandwidth.requests_cap}
              format={formatCount}
            />
          </div>

          <p className="mt-4 text-[11px] text-muted">
            updates every few seconds · resets monthly
          </p>
        </Card>
      </div>
    </div>
  )
}

// monthlyPeriod derives the display period (current UTC month) and the number
// of days until the next reset. Counters reset on the 1st of the month at
// 00:00 UTC (CLAUDE.md §12 cmd/usage-reset), so resets-in is the whole-day
// count from now to the first instant of next month. (Mirrors the helper in
// UsageTab.tsx; kept local to avoid coupling the two pages.)
function monthlyPeriod(): { period: string; resetsInDays: number } {
  const now = new Date()
  const period = now.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
  const nextReset = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  const msUntil = nextReset - now.getTime()
  const resetsInDays = Math.max(1, Math.ceil(msUntil / 86_400_000))
  return { period, resetsInDays }
}
